import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { logger } from "../../utils/logger";

interface Dish {
  id: string;
  name: string;
  is_available: boolean;
  [key: string]: unknown;
}

interface Message {
  type: "success" | "error";
  text: string;
}

interface StatusCounts {
  unavailable?: number;
  available?: number;
}

/**
 * Menu Availability Management Page - Simplified
 * Manages menu item availability without variant_combinations table
 */
function AdminMenuAvailability(): JSX.Element {
  const navigate = useNavigate();
  const [allDishes, setAllDishes] = useState<Dish[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<Record<string, boolean>>({});
  const [message, setMessage] = useState<Message | null>(null);
  const [showAllDishes, setShowAllDishes] = useState(true);

  // Dark Luxe color constants
  const COLORS = {
    burgundy: "#8B2634",
    amber: "#B8860B",
    goldGreen: "#9CAF88",
    accent: "#C59D5F",
  };

  const fetchAllDishes = useCallback(async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("menu_items")
        .select(
          "id, name, is_available, category_id, price, image_url, created_at, updated_at",
        )
        .order("name", { ascending: true });

      if (error) {
        logger.error("Error fetching menu items:", error);
        setAllDishes([]);
        return;
      }

      let filtered = (data || []) as Dish[];
      if (!showAllDishes) {
        filtered = filtered.filter((dish) => !dish.is_available);
      }

      setAllDishes(filtered);
    } catch (err) {
      logger.error("Error in fetchAllDishes:", err);
      setAllDishes([]);
    } finally {
      setLoading(false);
    }
  }, [showAllDishes]);

  useEffect(() => {
    void fetchAllDishes();

    const channel = supabase
      .channel("menu-items-availability")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "menu_items" },
        () => {
          void fetchAllDishes();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAllDishes]);

  const toggleAvailability = async (dishId: string, currentStatus: boolean) => {
    try {
      setUpdating((prev) => ({ ...prev, [dishId]: true }));
      setMessage(null);

      const { error: updateError } = await supabase
        .from("menu_items")
        .update({
          is_available: !currentStatus,
          updated_at: new Date().toISOString(),
        })
        .eq("id", dishId);

      if (updateError) throw new Error(updateError.message);

      const dish = allDishes.find((d) => d.id === dishId);
      setMessage({
        type: "success",
        text: `${dish?.name || "Item"} is now ${!currentStatus ? "available" : "unavailable"}`,
      });

      setTimeout(() => {
        fetchAllDishes();
        setMessage(null);
      }, 2000);
    } catch (err) {
      logger.error("Toggle availability error:", err);
      const error = err as Error;
      setMessage({ type: "error", text: `Failed: ${error.message}` });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setUpdating((prev) => {
        const updated = { ...prev };
        delete updated[dishId];
        return updated;
      });
    }
  };

  const getDishStatus = (dish: Dish): "unavailable" | "available" => {
    return dish.is_available ? "available" : "unavailable";
  };

  const statusCounts: StatusCounts = allDishes.reduce((acc, dish) => {
    const status = getDishStatus(dish);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as StatusCounts);

  const totalUnavailable = statusCounts.unavailable || 0;
  const totalDishes = allDishes.length;

  return (
    <main className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)]">
      <div className="mx-auto max-w-[1800px] px-4 sm:px-6 md:px-8 lg:px-12 py-6 sm:py-8 md:py-10 lg:py-12">
        {/* Header with Back Button */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/admin")}
            className="mb-4 px-4 py-2 rounded-lg bg-[rgba(197,157,95,0.15)] border border-[rgba(197,157,95,0.3)] hover:bg-[rgba(197,157,95,0.25)] hover:border-[rgba(197,157,95,0.5)] transition-all duration-200 cursor-pointer flex items-center gap-2 text-[var(--text-main)]"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Dashboard
          </button>
          <h1
            className="text-2xl sm:text-3xl font-bold"
            style={{ color: "var(--text-heading)" }}
          >
            Menu Availability Management
          </h1>
          <p
            className="text-sm sm:text-base mt-2"
            style={{ color: "var(--text-body-muted)" }}
          >
            Manage menu item availability
          </p>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-xl border ${
              message.type === "success"
                ? "bg-green-900/20 border-green-800 text-green-400"
                : "bg-red-900/20 border-red-800 text-red-400"
            }`}
          >
            {message.text}
          </div>
        )}

        {loading ? (
          <div
            className="rounded-2xl border backdrop-blur-xl p-6 animate-pulse"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              borderColor: "rgba(255, 255, 255, 0.1)",
            }}
          >
            <p className="text-[var(--text-muted)]">
              Loading menu availability...
            </p>
          </div>
        ) : (
          <div
            className="rounded-2xl border backdrop-blur-xl p-6"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              borderColor: "rgba(255, 255, 255, 0.1)",
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className="p-2 rounded-lg"
                  style={{
                    backgroundColor:
                      totalUnavailable > 0
                        ? `${COLORS.burgundy}20`
                        : `${COLORS.goldGreen}20`,
                  }}
                >
                  <svg
                    className="w-5 h-5"
                    style={{
                      color:
                        totalUnavailable > 0
                          ? COLORS.burgundy
                          : COLORS.goldGreen,
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
                <div>
                  <h3
                    className="text-lg font-semibold"
                    style={{ color: "var(--text-heading)" }}
                  >
                    Menu Availability
                  </h3>
                  <p
                    className="text-sm"
                    style={{ color: "var(--text-body-muted)" }}
                  >
                    {totalDishes} items • {totalUnavailable} unavailable
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowAllDishes(!showAllDishes)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: `${COLORS.accent}20`,
                  color: COLORS.accent,
                  border: `1px solid ${COLORS.accent}40`,
                }}
              >
                {showAllDishes ? "Show Unavailable Only" : "Show All Items"}
              </button>
            </div>

            {/* Items List */}
            {allDishes.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)]">
                {showAllDishes
                  ? "No menu items found."
                  : "All items are available!"}
              </div>
            ) : (
              <div className="space-y-3">
                {allDishes.map((dish) => {
                  const isAvailable = dish.is_available;
                  const isUpdating = updating[dish.id];

                  return (
                    <div
                      key={dish.id}
                      className="flex items-center justify-between p-4 rounded-xl border transition-all"
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                        borderColor: isAvailable
                          ? `${COLORS.goldGreen}30`
                          : `${COLORS.burgundy}30`,
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: isAvailable
                              ? COLORS.goldGreen
                              : COLORS.burgundy,
                          }}
                        />
                        <div>
                          <h4 className="font-medium text-[var(--text-main)]">
                            {dish.name}
                          </h4>
                          <p className="text-sm text-[var(--text-muted)]">
                            {isAvailable ? "Available" : "Unavailable"}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => toggleAvailability(dish.id, isAvailable)}
                        disabled={isUpdating}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                        style={{
                          backgroundColor: isAvailable
                            ? `${COLORS.burgundy}20`
                            : `${COLORS.goldGreen}20`,
                          color: isAvailable
                            ? COLORS.burgundy
                            : COLORS.goldGreen,
                          border: `1px solid ${isAvailable ? COLORS.burgundy : COLORS.goldGreen}40`,
                        }}
                      >
                        {isUpdating
                          ? "Updating..."
                          : isAvailable
                            ? "Mark Unavailable"
                            : "Mark Available"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

export default AdminMenuAvailability;
